import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { sdk } from "./sdk"

export type OrderTab = "all" | "unfulfilled" | "unpaid" | "open" | "archived"
export type OrderRow = {
  id: string
  display_id: number
  email: string | null
  customer_name: string | null
  status: string
  payment_status: string
  fulfillment_status: string
  total: number
  currency_code: string
  created_at: string
  archived: boolean
  kind: OrderKind
  channel: OrderChannel
  item_count: number
  delivery_method: string | null
  tags: string[]
  discount_codes: string | null
}
export type OrderKind = "one_time" | "subscription" | "renewal"
export type OrderChannel = "online_store" | "draft_order" | "subscriptions"
export const KIND_LABEL: Record<OrderKind, string> = { one_time: "One-time", subscription: "Subscription", renewal: "Renewal" }
export const CHANNEL_LABEL: Record<OrderChannel, string> = { online_store: "Online Store", draft_order: "Draft order", subscriptions: "Subscriptions app" }
/** The order's type from the order itself (detail page): same rules as the order index. */
export const kindOf = (o: any): OrderKind =>
  o?.metadata?.terify_sub_charge ? "renewal" : (o?.items ?? []).some((i: any) => i?.metadata?.terify_subscription?.plan_id) ? "subscription" : "one_time"
export const channelOf = (o: any): OrderChannel => (o?.metadata?.terify_sub_charge ? "subscriptions" : o?.metadata?.terify_draft_no || o?.metadata?.terify_draft_id ? "draft_order" : "online_store")
export type OrderSubs = {
  subscriptions: { id: string; title: string; variant_title: string | null; status: string; interval_unit: string; interval_count: number; next_charge_at: string | null; quantity: number; unit_price: number; currency_code: string }[]
  charge: { id: string; status: string; attempts: number; scheduled_for: string; amount: number | null } | null
}
export function useOrderSubscriptions(id: string, enabled: boolean) {
  return useQuery({ queryKey: ["order", id, "subscriptions"], enabled, queryFn: () => sdk.client.fetch<OrderSubs>(`/admin/terify/orders/${id}/subscriptions`) })
}

export type OrderFacets = { payment?: string; fulfillment?: string; type?: string; channel?: string; from?: string; to?: string; sort?: string; older_than_h?: string; return?: string }

export function useOrderList(p: { tab: OrderTab; q?: string; limit: number; offset: number } & OrderFacets) {
  return useQuery({
    queryKey: ["orders", "list", p],
    placeholderData: keepPreviousData,
    queryFn: () =>
      sdk.client.fetch<{ orders: OrderRow[]; count: number; counts: Record<OrderTab, number> }>("/admin/terify/orders", {
        query: { tab: p.tab, q: p.q?.trim() || undefined, payment: p.payment || undefined, fulfillment: p.fulfillment || undefined, type: p.type || undefined, channel: p.channel || undefined, from: p.from || undefined, to: p.to || undefined, older_than_h: p.older_than_h || undefined, return: p.return || undefined, sort: p.sort && p.sort !== "newest" ? p.sort : undefined, limit: p.limit, offset: p.offset },
      }),
  })
}

export type BulkOrderAction = { action: "archive" | "unarchive" } | { action: "add_tags" | "remove_tags"; tags: string[] }
export function useBulkOrders() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (v: { ids: string[] } & BulkOrderAction) => sdk.client.fetch<{ updated: number; skipped: number }>("/admin/terify/orders/bulk", { method: "POST", body: v }),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["orders"] })
      qc.invalidateQueries({ queryKey: ["order"] })
      qc.invalidateQueries({ queryKey: ["nav"] })
    },
  })
}

const ORDER_FIELDS = [
  "id,display_id,email,status,currency_code,created_at,canceled_at,payment_status,fulfillment_status",
  "total,original_total,summary,subtotal,item_total,shipping_total,tax_total,discount_total,+metadata",
  "*items,*items.detail,*items.variant,*items.adjustments",
  "*shipping_address,*billing_address,*shipping_methods,*customer",
  "*fulfillments,*fulfillments.items,*fulfillments.labels",
  "*payment_collections,*payment_collections.payments,*payment_collections.payments.refunds,*payment_collections.payments.captures",
].join(",")

export function useOrderDetail(id: string) {
  return useQuery({ queryKey: ["order", id], queryFn: () => sdk.admin.order.retrieve(id, { fields: ORDER_FIELDS } as any) })
}

/** Every order mutation refreshes the order, the lists, the timeline and the nav badge. */
function useOrderMutation<TVars>(id: string, fn: (v: TVars) => Promise<unknown>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["order", id] })
      qc.invalidateQueries({ queryKey: ["orders"] })
      qc.invalidateQueries({ queryKey: ["activity"] })
      qc.invalidateQueries({ queryKey: ["nav"] })
      qc.invalidateQueries({ queryKey: ["dashboard"] })
    },
  })
}

export type FulfillInput = { items: { id: string; quantity: number }[]; tracking_number?: string; tracking_url?: string; notify: boolean }

export function useFulfill(id: string) {
  return useOrderMutation(id, async (v: FulfillInput) => {
    const { order } = await sdk.admin.order.createFulfillment(id, { items: v.items, no_notification: !v.notify } as any, { fields: "id,*fulfillments" } as any)
    const created: any = (order as any).fulfillments?.[(order as any).fulfillments.length - 1]
    if (v.tracking_number && created?.id) {
      await sdk.admin.order.createShipment(id, created.id, {
        items: v.items,
        labels: [{ tracking_number: v.tracking_number, tracking_url: v.tracking_url || "#", label_url: "#" }],
        no_notification: !v.notify,
      } as any)
    }
  })
}

export function useAddTracking(id: string) {
  return useOrderMutation(id, (v: { fulfillmentId: string; items: { id: string; quantity: number }[]; tracking_number: string; tracking_url?: string; notify: boolean }) =>
    sdk.admin.order.createShipment(id, v.fulfillmentId, {
      items: v.items,
      labels: [{ tracking_number: v.tracking_number, tracking_url: v.tracking_url || "#", label_url: "#" }],
      no_notification: !v.notify,
    } as any)
  )
}

export function useMarkDelivered(id: string) {
  return useOrderMutation(id, (fulfillmentId: string) => sdk.admin.order.markAsDelivered(id, fulfillmentId, {} as any))
}

export function useCancelFulfillment(id: string) {
  return useOrderMutation(id, (fulfillmentId: string) => sdk.admin.order.cancelFulfillment(id, fulfillmentId, {} as any))
}

export function useCapture(id: string) {
  return useOrderMutation(id, (paymentId: string) => sdk.admin.payment.capture(paymentId, {} as any))
}

/**
 * Shopify's "Mark as paid" for a balance due (payment due later, or an order
 * edit that raised the total): records a manual payment for what's outstanding.
 */
export function useMarkPaid(id: string) {
  return useOrderMutation(id, async (v: { order: any; amount: number }) => {
    const open = (v.order.payment_collections ?? []).find((pc: any) => ["not_paid", "awaiting", "partially_authorized"].includes(pc.status) && Number(pc.amount) > 0)
    const pcId =
      open?.id ??
      (await sdk.client.fetch<{ payment_collection: any }>("/admin/payment-collections", { method: "POST", body: { order_id: id, amount: v.amount } })).payment_collection.id
    return sdk.client.fetch(`/admin/payment-collections/${pcId}/mark-as-paid`, { method: "POST", body: { order_id: id } })
  })
}

export function useRefund(id: string) {
  return useOrderMutation(id, (v: { paymentId: string; amount: number; note?: string }) => sdk.admin.payment.refund(v.paymentId, { amount: v.amount, note: v.note } as any))
}

export function useCancelOrder(id: string) {
  return useOrderMutation(id, () => sdk.admin.order.cancel(id))
}

export function useArchiveOrder(id: string) {
  return useOrderMutation(id, (archived: boolean) => sdk.client.fetch(`/admin/terify/orders/${id}/archive`, { method: archived ? "POST" : "DELETE" }))
}

export function useUpdateOrderMeta(id: string) {
  return useOrderMutation(id, (metadata: Record<string, unknown>) => sdk.admin.order.update(id, { metadata } as any))
}

export function useAddComment(id: string) {
  return useOrderMutation(id, (body: string) => sdk.client.fetch(`/admin/terify/orders/${id}/comments`, { method: "POST", body: { body } }))
}

/** Carriers and their tracking URL patterns (Shopify picks the carrier from the number). */
export const CARRIERS = [
  { value: "usps", label: "USPS", url: (n: string) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`, test: /^(94|93|92|95)\d{20}$|^\d{20,22}$/ },
  { value: "ups", label: "UPS", url: (n: string) => `https://www.ups.com/track?tracknum=${n}`, test: /^1Z[0-9A-Z]{16}$/i },
  { value: "fedex", label: "FedEx", url: (n: string) => `https://www.fedex.com/fedextrack/?trknbr=${n}`, test: /^\d{12}$|^\d{15}$/ },
  { value: "dhl", label: "DHL Express", url: (n: string) => `https://www.dhl.com/us-en/home/tracking.html?tracking-id=${n}`, test: /^\d{10}$/ },
  { value: "other", label: "Other", url: () => "", test: /$^/ },
] as const

export const guessCarrier = (n: string) => CARRIERS.find((c) => c.test.test(n.trim()))?.value ?? "other"
